/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Heading,
  HFlow,
  Icon,
  isEmpty,
  ModalSidebar,
  ModalSidebarPosition,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { resolveName } from 'components/form/final-form/hooks/useField'
import { differenceInMonths } from 'date-fns'
import { useGruposExamesQuery } from 'graphql/hooks.generated'
import { IdentidadeGeneroEnum, Procedimento, SexoEnum } from 'graphql/types.generated'
import { useCallback, useMemo } from 'react'
import { useField, useForm } from 'react-final-form'
import { toDate } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'

import { SolicitacaoExameModel } from '../../model-solicitacaoExames'
import { ExameListSelectFieldModel } from '../ExameListSelectField'
import { GrupoExamePopperButton } from './GrupoExamePopperButton'
import { orderAndFilterGruposExames } from './utils-grupoExames'

const name = metaPath<SolicitacaoExameModel>().examesSolicitados

interface GrupoExamesSidebarProps {
  dataNascimento: LocalDate
  sexo: SexoEnum
  identidadeGenero: IdentidadeGeneroEnum
  iniciadoEm: Instant
  position: ModalSidebarPosition
}

export const GrupoExamesSidebar = (props: GrupoExamesSidebarProps) => {
  const { position, dataNascimento, sexo, identidadeGenero, iniciadoEm } = props

  const theme = useTheme()
  const classes = styles(theme)

  const idadeNoAtendimento = differenceInMonths(toDate(iniciadoEm), toDate(dataNascimento))
  const sexoCidadao = isEmpty(identidadeGenero) ? sexo : null

  const {
    data: { gruposExame },
  } = useGruposExamesQuery({
    variables: {
      input: { sexo: sexoCidadao, dataNascimento, pageParams: { unpaged: true } },
    },
  })

  const items = useMemo(() => orderAndFilterGruposExames(gruposExame?.content, sexoCidadao, idadeNoAtendimento), [
    gruposExame,
    sexoCidadao,
    idadeNoAtendimento,
  ])

  const {
    input: { value },
  } = useField(resolveName(name), { subscription: { value: true } })

  const {
    mutators: { changeValue },
  } = useForm()

  const addItem = useCallback(
    (values) => {
      changeValue(name, (prevValue) => [...(prevValue || []), { ...values, _id: uuidv4() }])
    },
    [changeValue]
  )

  const removeItem = useCallback(
    (values) => {
      changeValue(name, (prevValue) => (prevValue || []).filter((item) => item._id !== values._id))
    },
    [changeValue]
  )

  const checkAllItems = useCallback(
    (exames: Procedimento[], isAllSelected: boolean) => {
      changeValue(name, (prevValues: ExameListSelectFieldModel[]): ExameListSelectFieldModel[] => {
        if (isAllSelected) {
          const newValuesIds = exames ? exames.map((exame) => exame.id) : []

          return (prevValues || []).filter((valueItem) => !newValuesIds.includes(valueItem.exame.id))
        } else {
          const prevValuesIds = prevValues ? prevValues.map(({ exame }) => exame.id) : []
          const newValues = exames.filter((exame) => !prevValuesIds.includes(exame.id))

          return [...(prevValues || []), ...newValues.map((exame) => ({ exame, _id: uuidv4() }))]
        }
      })
    },
    [changeValue]
  )

  return (
    <ModalSidebar position={position} style={classes.sidebar}>
      <VFlow vSpacing={0.5}>
        <HFlow hSpacing={0.25} alignItems='center'>
          <Heading level={5}>Grupo de exames</Heading>

          <Tooltip text='Os grupos de exames podem ser adicionados pelo Administrador municipal.' placement='top'>
            <Icon icon='infoCircleFilled' fill='normal' size={1} style={classes.tooltipIcon} />
          </Tooltip>
        </HFlow>

        {items.map((grupoExame) => (
          <GrupoExamePopperButton
            key={grupoExame.id}
            grupoExame={grupoExame}
            examesSelecionados={value}
            addItem={addItem}
            removeItem={removeItem}
            checkAllItems={checkAllItems}
          />
        ))}
      </VFlow>
    </ModalSidebar>
  )
}

const styles = (theme: Theme) => ({
  sidebar: css`
    padding-top: 5.75rem;
  `,
  tooltipIcon: css`
    &:hover {
      fill: ${theme.pallete.primary.main};
    }
  `,
})
